import { AUTH_ACTION_TYPES } from './types';
import authService from '../services/authService';

// Action creators for authentication

/**
 * Login user action
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @returns {Function} Redux thunk function
 */
export const loginUser = (credentials) => async (dispatch) => {
  // Reset all Redux state before login to ensure clean state
  dispatch({ type: AUTH_ACTION_TYPES.RESET_STATE });
  
  dispatch({ type: AUTH_ACTION_TYPES.LOGIN_REQUEST });

  try {
    const response = await authService.login(credentials);

    if (response.success) {
      // Ensure we have the required data structure
      const userData = response.data?.user || response.data;
      const accessToken = response.data?.token;

      dispatch({
        type: AUTH_ACTION_TYPES.LOGIN_SUCCESS,
        payload: {
          user: userData,
          accessToken: accessToken,
        }
      });

      // Return consistent structure for the component
      return {
        success: true,
        data: {
          user: userData,
          accessToken: accessToken,
        }
      };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
        payload: {
          error: response.message || 'Login failed',
          status: response.status
        }
      });

      return {
        success: false,
        error: response.message,
        exceptionName: response.exceptionName,
        status: response.status,
      };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return {
      success: false,
      error: error.message,
      exceptionName: error.exceptionName,
      status: error.status,
    };
  }
};

/**
 * Register user action
 * @param {Object} userData - User registration data
 * @returns {Function} Redux thunk function
 */
export const registerUser = (userData) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.REGISTER_REQUEST });

  try {
    const response = await authService.register(userData);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.REGISTER_SUCCESS,
        payload: {
          user: response.data.user,
          message: response.message
        }
      });

      return { success: true, data: response.data };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.REGISTER_FAILURE,
        payload: {
          error: response.message || 'Registration failed',
          status: response.status
        }
      });

      return { success: false, error: response.message };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.REGISTER_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { success: false, error: error.message };
  }
};

/**
 * Register subscriber user action
 * @param {Object} userData - Subscriber registration data
 * @returns {Function} Redux thunk function
 */
export const registerSubscriber = (userData) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.REGISTER_REQUEST });

  try {
    const response = await authService.registerSubscriber(userData);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.REGISTER_SUCCESS,
        payload: {
          user: response.data?.user || response.data,
          message: response.message || 'Registration successful'
        }
      });

      return {
        success: true,
        data: response.data,
        message: response.message
      };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.REGISTER_FAILURE,
        payload: {
          error: response.message || 'Registration failed',
          status: response.status
        }
      });

      return { 
        success: false, 
        error: response.message, 
        fields: response.error?.details, 
        status: response.status 
      };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.REGISTER_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { 
      success: false, 
      error: error.message, 
      fields: error.error?.details, 
      status: error.status 
    };
  }
};

/**
 * Logout user action
 * @returns {Function} Redux thunk function
 */
export const logoutUser = () => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.LOGOUT_REQUEST });

  try {
    const response = await authService.logout();

    dispatch({
      type: AUTH_ACTION_TYPES.LOGOUT_SUCCESS,
      payload: {
        message: response.message || 'Logged out successfully'
      }
    });

    // Reset all Redux state after logout
    dispatch({ type: AUTH_ACTION_TYPES.RESET_STATE });

    return { success: true };
  } catch (error) {
    // Even if logout fails on server, clear local state
    dispatch({
      type: AUTH_ACTION_TYPES.LOGOUT_SUCCESS,
      payload: {
        message: 'Logged out successfully'
      }
    });

    // Reset all Redux state after logout
    dispatch({ type: AUTH_ACTION_TYPES.RESET_STATE });

    return { success: true };
  }
};


/**
 * Forgot password action
 * @param {string} email - User email
 * @returns {Function} Redux thunk function
 */
export const forgotPassword = (email) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.FORGOT_PASSWORD_REQUEST });

  try {
    const response = await authService.forgotPassword(email);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.FORGOT_PASSWORD_SUCCESS,
        payload: {
          message: response.message || 'Password reset email sent'
        }
      });

      return { success: true, data: response.data };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.FORGOT_PASSWORD_FAILURE,
        payload: {
          error: response.message || 'Failed to send reset email',
          status: response.status
        }
      });

      return { success: false, error: response.message };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.FORGOT_PASSWORD_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { success: false, error: error.message };
  }
};

/**
 * Verify reset password token (valid / invalid / expired).
 * Uses authService; returns { valid, message?, expired? } for component handling.
 * @param {string} token - Reset token from link
 * @returns {Promise<Object>} { valid, message?, expired? }
 */
export const verifyResetToken = (token) => async () => {
  const invalidResponse = (message, expired = false) => ({
    valid: false,
    message,
    expired,
  });

  if (!token || (typeof token === 'string' && !token.trim())) {
    return invalidResponse('Token is required. Please use the link from your reset password email.');
  }

  try {
    const result = await authService.verifyResetToken(token);
    return {
      valid: result.valid === true,
      message: result.message ?? (result.valid ? 'Link is valid.' : 'Invalid or expired link.'),
      expired: result.expired === true,
    };
  } catch (error) {
    return invalidResponse(
      error?.message || 'Unable to verify link. Please try again or request a new reset link.'
    );
  }
};

/**
 * Reset password action
 * @param {Object} resetData - Reset password data
 * @returns {Function} Redux thunk function
 */
export const resetPassword = (resetData) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.RESET_PASSWORD_REQUEST });

  try {
    const response = await authService.resetPassword(resetData);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.RESET_PASSWORD_SUCCESS,
        payload: {
          message: response.message || 'Password reset successfully'
        }
      });

      return { success: true, data: response.data };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.RESET_PASSWORD_FAILURE,
        payload: {
          error: response.message || 'Password reset failed',
          status: response.status
        }
      });

      return { success: false, error: response.message };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.RESET_PASSWORD_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { success: false, error: error.message };
  }
};

/**
 * Verify confirm token (check if email confirmation link is valid).
 * @param {string} userId - User ID from link
 * @param {string} token - Email verification token from link
 * @returns {Promise<Object>} { valid, message, alreadyVerified, expired, rol }
 */
export const verifyConfirmToken = (userId, token) => async () => {
  if (!userId || !token) {
    return {
      valid: false,
      message: 'userId and token are required.',
      alreadyVerified: false,
      expired: false,
      rol: undefined,
    };
  }
  try {
    const result = await authService.verifyConfirmToken(userId, token);
    return {
      valid: result.valid === true,
      message: result.message,
      alreadyVerified: result.data?.alreadyVerified === true,
      expired: result.data?.expired === true,
      rol: result.data?.rol,
    };
  } catch (error) {
    return {
      valid: false,
      message: error?.message || 'Invalid link.',
      alreadyVerified: false,
      expired: false,
      rol: undefined,
    };
  }
};

/**
 * Verify email action
 * @param {string} token - Email verification token
 * @returns {Function} Redux thunk function
 */
export const verifyEmail = (token) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.VERIFY_EMAIL_REQUEST });

  try {
    const response = await authService.verifyEmail(token);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.VERIFY_EMAIL_SUCCESS,
        payload: {
          message: response.message
        }
      });
      return { success: true, message: response.message, data: response.data };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.VERIFY_EMAIL_FAILURE,
        payload: {
          error: response.message || 'Email verification failed',
          status: response.status
        }
      });

      return { success: false, error: response.message };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.VERIFY_EMAIL_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { success: false, error: error.message };
  }
};

/**
 * Resend verification email action
 * @param {Object} data - User data
 * @param {Object} payload - User data
 * @param {boolean} payload.isUpdateEmail - Whether this is an email update
 * @param {string} payload.oldEmail - Previous email address
 * @returns {Promise} Promise that resolves with response data
 */
export const resendVerification = async (payload) => {
  try {
    const response = await authService.resendVerification(payload);
    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message };
    }
  } catch (error) {
      return { success: false, error: error.message };
    }
};

/**
 * Check username availability action
 * @param {string} username - Username to check
 * @returns {Function} Redux thunk function
 */
export const checkUsername = (username) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.CHECK_USERNAME_REQUEST });

  try {
    const response = await authService.checkUsername(username);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.CHECK_USERNAME_SUCCESS,
        payload: {
          username: username,
          isAvailable: response.data.isValid,
          message: response.message
        }
      });

      return {
        success: true,
        data: {
          isValid: response.data.isValid,
          username: username
        }
      };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.CHECK_USERNAME_FAILURE,
        payload: {
          error: response.message || 'Username check failed',
          status: response.status
        }
      });

      return { success: false, error: response.message };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.CHECK_USERNAME_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { success: false, error: error.message };
  }
};

/**
 * Register creator action
 * @param {Object} registrationData - Creator registration data
 * @param {string} subType - Subscription type
 * @returns {Function} Redux thunk function
 */
export const registerCreator = (registrationData, subType) => async (dispatch) => {
  dispatch({ type: AUTH_ACTION_TYPES.REGISTER_REQUEST });

  try {
    const response = await authService.registerCreator(registrationData, subType);

    if (response.success) {
      dispatch({
        type: AUTH_ACTION_TYPES.REGISTER_SUCCESS,
        payload: {
          user: response.data.user,
          message: response.message
        }
      });

      return { success: true, data: response.data };
    } else {
      dispatch({
        type: AUTH_ACTION_TYPES.REGISTER_FAILURE,
        payload: {
          error: response.message || 'Creator registration failed',
          status: response.status
        }
      });

      return { 
        success: false, 
        error: response.message, 
        fields: response.error?.details, 
        status: response.status 
      };
    }
  } catch (error) {
    dispatch({
      type: AUTH_ACTION_TYPES.REGISTER_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { 
      success: false, 
      error: error.message, 
      fields: error.error?.details, 
      status: error.status 
    };
  }
};

/**
 * Clear authentication errors action
 * @returns {Object} Action object
 */
export const clearAuthErrors = () => ({
  type: AUTH_ACTION_TYPES.CLEAR_AUTH_ERRORS
});

/**
 * Set authentication status action
 * @param {boolean} isAuthenticated - Authentication status
 * @returns {Object} Action object
 */
export const setAuthStatus = (isAuthenticated) => ({
  type: AUTH_ACTION_TYPES.SET_AUTH_STATUS,
  payload: { isAuthenticated }
});

/**
 * Reset all Redux state action
 * @returns {Object} Action object
 */
export const resetState = () => ({
  type: AUTH_ACTION_TYPES.RESET_STATE
});
